import { Option, Schema } from 'effect';

import { PriceCents } from '@vs-shop/domain/price';
import { StockStatus } from '@vs-shop/domain/stock';
import { CartQuantity } from '@vs-shop/domain/product/cartQuantity';

import { ImageTemplate } from '../image/ImageTemplate';

/**
 * The curated product vocabulary the capability tier answers in.
 *
 * These are engine-owned and tenant-neutral. A tenant adapter decodes its own
 * generated OpenAPI schema and maps onto these; nothing below a loader ever
 * sees a generated type, a JSON-LD envelope or a Sylius field name.
 *
 * The shapes are minimums, not supersets. A field is here because a live page
 * reads it today. A field one backend cannot answer is an `Option` here, and a
 * field neither page reads is absent — a curated schema that grows to match one
 * backend's payload has stopped being curated.
 *
 * Absence is `Option`, never `null`. The encoded side accepts `null` and
 * `undefined` so an adapter can map straight from a nullable payload, and the
 * decoded side is an `Option` a mapper resolves once, at the edge of the page.
 */

/**
 * The season a tyre is sold for.
 *
 * A shop that sells no tyres answers `None` for every product, which is what
 * makes this legal in a neutral shape at all: the field describes a product
 * that has a season, not a shop that has seasons.
 */
export const ProductSeason = Schema.Literals(['summer', 'winter', 'all_season']);
export type ProductSeason = typeof ProductSeason.Type;

/**
 * What a variant's availability is called.
 *
 * The vocabulary is `@vs-shop/domain`'s, so the capability tier and the stock
 * helpers that render it cannot drift apart. A backend that reports
 * availability as a bare boolean supplies `None` here and `inStock` instead.
 */
export const ProductStockStatus = Schema.Literals([
  StockStatus.IN_STOCK,
  StockStatus.SOLD_OUT,
  StockStatus.PRE_ORDER,
  StockStatus.SUPPLIER_STOCK,
]);
export type ProductStockStatus = typeof ProductStockStatus.Type;

/**
 * One product image, at the position the backend orders its gallery by.
 *
 * `template` is the backend's image template, with its `{filter}` placeholder
 * still in place. The adapter picks no rendition. The slot that renders the
 * image picks the filter with `resolveImageUrl`, because only the slot knows
 * the box the image fills.
 */
export const ProductImage = Schema.Struct({
  position: Schema.Finite.check(Schema.isInt()),
  template: ImageTemplate,
});
export type ProductImage = typeof ProductImage.Type;

const ListingDate = Schema.TaggedStruct('Date', { value: Schema.String });

/**
 * One of a product's parameters, as the detail view lists them.
 *
 * A parameter with no name is not a parameter a page can render, so the
 * adapter drops it rather than sending an unnamed row on. The value keeps all
 * three shapes a backend sends — a string, a measurement, a select's choices —
 * because collapsing them to a string is a formatting rule, and formatting is
 * the mapper's job.
 *
 * `unit` is what the value is measured in, exactly as the backend states it.
 * `None` means the value stands alone. A unit the catalogue states wrongly is
 * catalogue data, so no adapter and no mapper corrects it.
 */
export const ProductAttributeValue = Schema.Union([
  Schema.String,
  Schema.Finite,
  Schema.Array(Schema.String),
]);
export type ProductAttributeValue = typeof ProductAttributeValue.Type;

export const ProductAttribute = Schema.Struct({
  name: Schema.String,
  value: ProductAttributeValue,
  unit: Schema.OptionFromNullishOr(Schema.String),
});
export type ProductAttribute = typeof ProductAttribute.Type;

/**
 * A promotion code the backend says applies to the offer: the code, the price
 * it leaves and the rate it takes off. How the saving reads is a tenant rule.
 */
export const ProductOfferCoupon = Schema.Struct({
  code: Schema.String,
  promotedPrice: PriceCents,
  salePercentage: Schema.Finite,
});
export type ProductOfferCoupon = typeof ProductOfferCoupon.Type;

const decodePriceCents = Schema.decodeUnknownOption(PriceCents);

/**
 * A backend's coupon rows as the offer carries them. A code whose promoted
 * price is not a price states no saving, so it drops. Every tenant adapter maps
 * its rows through this, so the tenants cannot disagree on which code counts.
 */
export const toProductOfferCoupons = (
  rows: ReadonlyArray<{
    readonly code: string;
    readonly promotedPrice: number;
    readonly salePercentage: number;
  }>
): ReadonlyArray<ProductOfferCoupon> =>
  rows.flatMap((row) =>
    Option.toArray(
      Option.map(decodePriceCents(row.promotedPrice), (promotedPrice) => ({
        code: row.code,
        promotedPrice,
        salePercentage: row.salePercentage,
      }))
    )
  );

/**
 * A product as a listing returns it — everything a card renders, and no more.
 *
 * Money is in minor units, branded `PriceCents`, so no caller formats a bare
 * number. `originalPrice` is the pre-discount figure exactly as the backend
 * states it; whether it is worth showing is a display rule and stays in the
 * mapper.
 *
 * `href` is the backend's own product URL. A storefront that routes products
 * itself ignores it and builds its own; one that follows the backend's routing
 * uses it. Both are one line in a mapper, which is why the port carries the
 * fact rather than the decision.
 *
 * `image` is the main image's backend template. The card that renders it picks
 * the filter with `resolveImageUrl`.
 */
export const ProductSummary = Schema.Struct({
  code: Schema.String,
  variantCode: Schema.OptionFromNullishOr(Schema.String),
  defaultCartQuantity: Schema.OptionFromOptionalKey(CartQuantity),
  name: Schema.OptionFromNullishOr(Schema.String),
  subtitle: Schema.OptionFromNullishOr(Schema.String),
  season: Schema.OptionFromNullishOr(ProductSeason),
  href: Schema.OptionFromNullishOr(Schema.String),
  image: Schema.OptionFromNullishOr(ImageTemplate),
  price: Schema.OptionFromNullishOr(PriceCents),
  priceWithoutTax: Schema.OptionFromNullishOr(PriceCents),
  originalPrice: Schema.OptionFromNullishOr(PriceCents),
  inStock: Schema.OptionFromNullishOr(Schema.Boolean),
  /**
   * How many of the variant a shopper can order, or `None` where the backend
   * tracks no count. Zero and `None` are different answers: zero is nothing on
   * hand, `None` is a variant whose stock is untracked and therefore unbounded.
   */
  maxOrderable: Schema.OptionFromNullishOr(Schema.Finite.check(Schema.isInt())),
  isFavourite: Schema.OptionFromNullishOr(Schema.Boolean),
  listingFacts: Schema.optionalKey(
    Schema.Struct({
      slug: Schema.NullOr(Schema.String),
      metaTitle: Schema.NullOr(Schema.String),
      description: Schema.NullOr(Schema.String),
      imagePath: Schema.NullOr(Schema.String),
      ageRestriction: Schema.Literals(['none', '18+']),
      available: Schema.NullOr(Schema.Boolean),
      stockStatus: Schema.NullOr(ProductStockStatus),
      dispatchDate: Schema.NullOr(ListingDate),
      localWarehouse: Schema.Array(
        Schema.Struct({
          type: Schema.NullOr(Schema.String),
          quantity: Schema.NullOr(Schema.Finite),
          dispatchDate: Schema.NullOr(ListingDate),
        })
      ),
      coupons: Schema.Array(
        Schema.Struct({
          code: Schema.String,
          promotedPrice: PriceCents,
          style: Schema.Literals(['none', 'clickable', 'static']),
        })
      ),
      labels: Schema.Array(
        Schema.Struct({
          code: Schema.String,
          color: Schema.String,
          textColor: Schema.String,
          icon: Schema.NullOr(Schema.String),
          name: Schema.NullOr(Schema.String),
        })
      ),
    })
  ),
  coupons: Schema.Array(ProductOfferCoupon),
});
export type ProductSummary = typeof ProductSummary.Type;
